/**
 * Centralised, validated resolver for the Stockaily analytics
 * application environment.
 *
 * Returns exactly one of:
 *   "production" | "staging" | "preview" | "development" | "test"
 *
 * Precedence (first valid value wins):
 *
 *   1.  `window.__STOCKAILY_RUNTIME_CONFIG__.appEnvironment`
 *       — baked into `/runtime-config.js` by the Docker builder.
 *   2.  `process.env.REACT_APP_ENV`
 *       — CRA build-time constant, substituted by webpack DefinePlugin.
 *   3.  Strict hostname mapping of `window.location.hostname`.
 *   4.  `"test"` when running under Jest (NODE_ENV === "test").
 *   5.  Fail-safe fallback → `"development"`.
 *
 * Invariants
 * ----------
 * * ``NODE_ENV === "production"`` alone **MUST NEVER** classify the
 *   application as Stockaily production.  CRA sets `NODE_ENV=production`
 *   for every optimised bundle — including the staging image — so it
 *   is NOT a trustworthy environment signal.  It is intentionally
 *   missing from the precedence list except as `"test"` detection.
 * * Any unknown / malformed input falls back to `"development"`,
 *   never `"production"`.
 * * Only hostnames belonging to the strict Stockaily / Emergent
 *   allowlist map to `"production"` / `"staging"` / `"preview"`.
 * * The raw hostname is NEVER sent to PostHog; only the resolved
 *   bounded label is.
 */

const VALID_ENVS = new Set([
  "production",
  "staging",
  "preview",
  "development",
  "test",
]);

function _normalise(label) {
  if (typeof label !== "string") return null;
  const s = label.trim().toLowerCase();
  return VALID_ENVS.has(s) ? s : null;
}

/**
 * Strict hostname → environment mapping.  Any hostname not matched
 * here resolves to `null`; the caller falls back to `development`.
 */
function _resolveFromHostname(hostname) {
  if (typeof hostname !== "string" || !hostname) return null;
  const h = hostname.trim().toLowerCase();

  // Known Stockaily production hosts (exact match).
  if (h === "stockaily.ai" || h === "www.stockaily.ai") return "production";

  // Known Stockaily staging host (exact match).
  if (h === "staging.stockaily.ai") return "staging";

  // Known Emergent preview host patterns already used elsewhere in
  // this repository (see pages/OwnerConsole.js).  Preview is the
  // Emergent in-platform environment used before anything is pushed
  // to Scaleway.
  if (/\.preview\.emergentagent\.com$/i.test(h)) return "preview";
  if (/^preview\.emergentagent\.com$/i.test(h)) return "preview";

  // Localhost / loopback → development.
  if (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "::1" ||
    h === "0.0.0.0" ||
    /\.localhost$/i.test(h)
  ) {
    return "development";
  }

  // Anything else — unknown host — must NOT be inferred as
  // production.  Return null so the caller falls back to
  // "development".
  return null;
}

/**
 * Pure resolver — the arguments are injected so tests can exercise
 * every branch without manipulating the global `window`.
 */
export function resolveAppEnvironment({
  runtimeConfig = (typeof window !== "undefined" &&
    window.__STOCKAILY_RUNTIME_CONFIG__) || null,
  buildTimeEnv = (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_ENV) || "",
  nodeEnv = (typeof process !== "undefined" &&
    process.env &&
    process.env.NODE_ENV) || "",
  hostname = (typeof window !== "undefined" &&
    window.location &&
    window.location.hostname) || "",
} = {}) {
  // (1) Runtime-config has the highest priority — it is baked into
  //     the deployed image and cannot be spoofed by the client.
  if (runtimeConfig && typeof runtimeConfig === "object") {
    const rc = _normalise(runtimeConfig.appEnvironment);
    if (rc) return rc;
  }

  // (2) Build-time REACT_APP_ENV — Preview / local dev / unit tests.
  const bt = _normalise(buildTimeEnv);
  if (bt) return bt;

  // (3) Strict hostname mapping — authoritative when runtime config
  //     and build-time env are both absent or invalid.  Covers the
  //     "same compiled image promoted between environments" case:
  //     if runtime config is missing / unparseable, the deployed
  //     hostname decides.
  const hs = _resolveFromHostname(hostname);
  if (hs) return hs;

  // (4) Test environment.
  if ((nodeEnv || "").toLowerCase() === "test") return "test";

  // (5) Fail-safe fallback — NEVER "production" from an unknown
  //     origin.  NODE_ENV=production alone does NOT justify
  //     classifying this app as Stockaily production.
  return "development";
}

export const APP_ENVIRONMENT_VALUES = Object.freeze([
  "production", "staging", "preview", "development", "test",
]);

/**
 * Default export for the common use case — resolver without
 * overrides, snapshotted once per module import.  Analytics code
 * calls this on every init to pick up any late-binding of the
 * runtime config.
 */
export default resolveAppEnvironment;
